// src/lib/featureAxes.js — which AXIS a building fact belongs to, and whether that axis is
// mutually exclusive (two observed values = the sources disagree) or additive (two values are
// both true at once).
//
// Why this exists: the Desk used to decide by PREFIX — `/^(cls_|class_)/` meant "building
// status". That is wrong twice over (designer ruling 2026-07-24, after the live vocab was
// dumped from /api/core/features):
//   • `cls_` is not one axis. cls_new_built / cls_old_built / cls_under_constr are the
//     building-AGE axis; cls_business_class (market tier) and cls_townhouse (building form)
//     sit in the same DB subcategory but are ADDITIVE — a townhouse can also be newly built,
//     so "ახალი აშენებული · თაუნჰაუსი" is two facts, never a conflict.
//   • `class_1…8` is the apartment-CLASS ladder, a different axis again. The prefix rule
//     rendered it in the Building-status row even though the fill-in offers those codes as
//     condition, and the match scorer counts them as the condition ladder
//     (`core/match-score.mjs` → CONDITION_LADDER_RE = /^(reno_|class_[1-8]_)/).
//
// The forthcoming CONFLICT row keys off `isExclusiveAxis`, NOT off a prefix. Anything that
// needs to know "are these two codes allowed to coexist" asks here, so display, scoring and
// the conflict rule can never drift apart again.
//
// `vocab` is the /api/core/features array (code, category, subcategory, …). It is the source
// of truth; the code-shape fallbacks only cover the window before the fetch lands, and a code
// minted after this file was written.

export const AXIS = {
  PROJECT: 'project',            // building series — ჩეხური, ხრუშოვკა, ქალაქური …  (17 codes)
  BUILD_STATUS: 'build_status',  // building/classification — age + additive building tags
  APT_CLASS: 'apt_class',        // apartment quality ladder class_1…class_8
  CONDITION: 'condition',        // finish / renovation state — reno_*, frame_*, condition cat
  FURNISHING: 'furnishing',      // how furnished — furn_fully / furn_partial / furn_none (0764)
}

// The furnishing DEGREE codes (0764). Exactly one describes a flat, so they are an axis and not
// three tags — which is what keeps them out of the AND-ed checkbox panel and gives them a ladder.
// ⚠️ NOT a prefix rule: `furnished` (the plain yes/no) and `furniture_partial_removed` also start
// with `furn`, and folding either into this axis would make an ordinary tag clear the ladder.
const FURNISH_DEGREE_CODES = new Set(['furn_fully', 'furn_partial', 'furn_none'])

// The only mutually-exclusive members of building/classification. Everything else in that
// subcategory is a plain additive tag.
const BUILD_STATUS_EXCLUSIVE = new Set(['cls_new_built', 'cls_old_built', 'cls_under_constr'])

export function axisOf(code, vocab = []) {
  const c = String(code || '')
  if (!c) return null
  const f = vocab.find(v => v.code === c)
  const cat = f?.category || null
  const sub = f?.subcategory || null
  if (sub === 'project' || /^proj_/.test(c)) return AXIS.PROJECT
  if (cat === 'apartment_class' || /^class_[1-8]_/.test(c)) return AXIS.APT_CLASS
  if ((cat === 'building' && sub === 'classification') || /^cls_/.test(c)) return AXIS.BUILD_STATUS
  if (FURNISH_DEGREE_CODES.has(c)) return AXIS.FURNISHING
  if (['condition', 'renovation', 'frame'].includes(cat) || /^(reno_|frame_)/.test(c)) return AXIS.CONDITION
  return null
}

// DETECTION KEYS OFF AXIS IDENTITY; RENDERING FOLLOWS THE EXISTING HOST ROW (designer,
// 2026-07-24). An axis does not imply a row of its own: APT_CLASS is its own axis for conflict
// purposes, but its amber flag renders in the CONDITION row — the row that already hosts it,
// because that is where the fill-in writes it and where match-score's CONDITION_LADDER_RE
// scores it. Same treatment, different host row. Adding a quality row would be a facts-table
// layout change (a separate ask), never a side effect of conflict detection.
//
// Is this code on an axis where a second, different value means the SOURCES DISAGREE?
// Scoped to exactly the three axes the designer ruled on (2026-07-24). CONDITION is
// deliberately NOT here: its coarse parents overlap their own children — `renovated` is a
// parent of reno_new, not a sibling (see the note above isConditionFeature in
// core/match-score.mjs) — so a naive "two values = conflict" rule would fire on data that is
// merely coarse + fine. Needs its own ruling before it joins.
// ⚠️ FURNISHING IS DELIBERATELY ABSENT BELOW, for the same reason CONDITION is: being an axis
// (one answer per flat) is not the same as "a second value means the sources DISAGREE". A
// photograph reports what is PRESENT; an advert's "partial" usually means something is being
// TAKEN AWAY, which no camera can see. So Lynceus saying `furn_fully` and the text saying
// `furn_partial` is two questions wearing one word, not a contradiction to flag in the facts
// table. Joining this list is an owner ruling nobody has made.
export function isExclusiveAxis(code, vocab = []) {
  const a = axisOf(code, vocab)
  if (a === AXIS.BUILD_STATUS) return BUILD_STATUS_EXCLUSIVE.has(String(code))
  return a === AXIS.PROJECT || a === AXIS.APT_CLASS
}

// Source kinds that represent a HUMAN DECISION about this property, as opposed to something
// a machine read off a page. A human pick settles an axis: it is the "ეს სწორია" override.
// `registry` is deliberately NOT here — it is authoritative-sounding but it is still a
// document read, and nobody has ruled on whether it outranks an agent standing in the flat.
const HUMAN_SOURCE_KINDS = new Set(['agent', 'owner', 'admin'])

const newer = (a, b) => new Date(a?.last_seen_at || 0) - new Date(b?.last_seen_at || 0)
// Best row for ONE value: highest confidence, tie → most recently seen (the designer's rule).
const bestRow = rows => rows.slice().sort((a, b) =>
  (Number(b.confidence) || 0) - (Number(a.confidence) || 0) || newer(b, a))[0]

// THE CONFLICT RULE (board §9, 2026-07-24). Feed it `merged.features` — the per-row provenance
// the lineage endpoint ships — and get one entry per exclusive axis that carries ≥1 value.
//
//   { axis, winner, candidates[], flagged, resolvedBy }
//
// • winner   — the ONE value to render. Never a join of two, never blank.
// • flagged  — show the amber "N წყარო არ ეთანხმება" affordance.
// • resolvedBy — the source_kind of the human row that settled it, else null.
//
// ⚠️ Why `flagged` is not simply "more than one value": commit_feature is APPEND-ONLY (see the
// note above FillFactPill). An agent picking the right value ADDS a row; it cannot retract the
// scrape that disagreed. So "the flag clears after the override" has to be expressed here, in
// the rule, or the conflict would re-render forever with the agent's own answer sitting in it.
// A human pick therefore wins and silences the axis — until a scrape SEEN LATER still disagrees,
// which re-raises it exactly as the board specifies.
export function axisConflicts(featureRows = [], vocab = []) {
  const byAxis = new Map()
  for (const r of featureRows) {
    const code = r?.feature_key
    if (!isExclusiveAxis(code, vocab)) continue          // additive tags never participate
    const a = axisOf(code, vocab)
    if (!byAxis.has(a)) byAxis.set(a, [])
    byAxis.get(a).push(r)
  }
  const out = []
  for (const [axis, rows] of byAxis) {
    const codes = [...new Set(rows.map(r => r.feature_key))]
    const humanRows = rows.filter(r => HUMAN_SOURCE_KINDS.has(r.source_kind))
    if (humanRows.length) {
      const decision = humanRows.slice().sort((a, b) => newer(b, a))[0]
      // A machine reading taken AFTER the human decided, still disagreeing → re-raise.
      const laterDisagreement = rows.some(r => !HUMAN_SOURCE_KINDS.has(r.source_kind)
        && r.feature_key !== decision.feature_key && newer(r, decision) > 0)
      out.push({ axis, winner: decision, candidates: rows, flagged: laterDisagreement,
        resolvedBy: decision.source_kind })
      continue
    }
    out.push({ axis, winner: bestRow(rows), candidates: rows, flagged: codes.length > 1,
      resolvedBy: null })
  }
  return out
}

// Codes on the same exclusive axis as `code`, drawn from an observed set — i.e. the candidate
// members of one conflict. Fewer than 2 = no conflict.
export function sameAxisPeers(code, codes = [], vocab = []) {
  if (!isExclusiveAxis(code, vocab)) return []
  const a = axisOf(code, vocab)
  return codes.filter(x => x !== code && isExclusiveAxis(x, vocab) && axisOf(x, vocab) === a)
}
